import React, { FC } from 'react'
import BasicTeaser, {
  IBasicTeaserTranslatedContent,
  IBasicTeaserSectionProps,
  TeaserLayoutVariant,
} from 'src/components/sections/teaser/basic-teaser'
import SectionHeader, { ISectionListItem } from 'src/components/ui/SectionHeader'
import { GLOBAL_PREVIEW_MODE } from 'src/helpers/preview'

export interface ITeaserWithHighlightContent extends IBasicTeaserTranslatedContent {
  content: string
  ctaLink?: string
  ctaText?: string
  cta2Link?: string
  cta2Text?: string
  ctaLink2?: string
  ctaText2?: string
  sectionName: string
  subContent?: string
  list?: ISectionListItem[]
  gridList?: ISectionListItem[]
  ctaVariant?: 'button' | 'link'
  linkText?: string
  linkUrl?: string
  thirdPartOfTitle?: string
  interactiveRowsVariant?: 'default' | 'compact' | 'radar'
  interactiveRowsSize?: 'default' | 'compact' | 'radar'
}

export interface ITeaserWithHighlightProps extends Omit<IBasicTeaserSectionProps, 'translationContent' | 'children' | 'headerChildren'> {
  translationContent: ITeaserWithHighlightContent
  gridItems?: boolean | ISectionListItem[]
  isGridList?: boolean
  listVariant?: 'list' | 'grid' | 'interactive-rows' | 'interactive-rows-compact' | 'interactive-rows-radar'
  listLayout?: 'list' | 'grid' | 'interactive-rows' | 'interactive-rows-compact' | 'interactive-rows-radar'
  interactiveRowsVariant?: 'default' | 'compact' | 'radar'
  interactiveRowsSize?: 'default' | 'compact' | 'radar'
  hideGridMarker?: boolean
  gridTextSize?: string
  centerGridText?: boolean
  disableHoverOnMobile?: boolean
  disableGridHover?: boolean
  ctaVariant?: 'button' | 'link'
  linkText?: string
  linkUrl?: string
  cta2Text?: string
  cta2Link?: string
  ctaText2?: string
  ctaLink2?: string
}

const TeaserWithHighlight: FC<ITeaserWithHighlightProps> = ({
  translationContent,
  isPreview,
  previewImageUrl,
  layoutVariant = 'split',
  showStatistics = false,
  hideImage = false,
  imageFit = 'natural',
  splitRatio = '1/2',
  imageMarginTop,
  imageAlignTop = false,
  gridItems,
  isGridList = false,
  listVariant = 'list',
  listLayout = 'list',
  interactiveRowsVariant,
  interactiveRowsSize,
  hideGridMarker,
  gridTextSize,
  centerGridText,
  disableHoverOnMobile,
  disableGridHover,
  paddingClassName,
  ctaVariant = 'button',
  linkText,
  linkUrl,
  cta2Text,
  cta2Link,
  ctaText2,
  ctaLink2,
  ...basicTeaserProps
}) => {
  const isPreviewMode = isPreview !== undefined ? isPreview : GLOBAL_PREVIEW_MODE

  const effectiveCtaVariant = ctaVariant || translationContent?.ctaVariant || 'button'
  const isLinkCta =
    effectiveCtaVariant === 'link' ||
    Boolean(linkText && linkUrl) ||
    Boolean(translationContent?.linkText && translationContent?.linkUrl)

  const effectiveLinkText = isLinkCta
    ? (linkText || translationContent?.linkText || translationContent.ctaText)
    : undefined
  const effectiveLinkUrl = isLinkCta
    ? (linkUrl || translationContent?.linkUrl || translationContent.ctaLink)
    : undefined

  const isInteractiveRows =
    listLayout === 'interactive-rows' ||
    listLayout === 'interactive-rows-compact' ||
    listVariant === 'interactive-rows' ||
    listVariant === 'interactive-rows-compact'
  const effectiveLayoutVariant: TeaserLayoutVariant =
    layoutVariant || ((hideImage || isGridList || isInteractiveRows) ? 'full-width' : 'split')

  const effectivePreviewImage =
    previewImageUrl ||
    `/images/placeholders/illustration/${translationContent.sectionName?.toLowerCase().replace(/\s+/g, '')}.webp`

  const effectiveCta2Text = cta2Text || ctaText2 || translationContent.cta2Text || translationContent.ctaText2
  const effectiveCta2Link = cta2Link || ctaLink2 || translationContent.cta2Link || translationContent.ctaLink2

  const shouldSplitHeader = isLinkCta && !hideImage

  const headerNode = shouldSplitHeader ? (
    <SectionHeader
      subtitle={translationContent.subtitle}
      titlePart1={translationContent.firstPartOfTitle}
      titlePart2={translationContent.secondPartOfTitle}
      titlePart3={translationContent.thirdPartOfTitle}
      ctaVariant="link"
      linkText={effectiveLinkText}
      linkUrl={effectiveLinkUrl}
      titleOnly
    />
  ) : undefined

  return (
    <BasicTeaser
      {...basicTeaserProps}
      headerChildren={headerNode}
      translationContent={translationContent}
      isPreview={isPreviewMode}
      previewImageUrl={effectivePreviewImage}
      layoutVariant={effectiveLayoutVariant}
      showStatistics={showStatistics}
      hideImage={hideImage}
      imageFit={imageFit}
      splitRatio={shouldSplitHeader ? '1/2' : splitRatio}
      imageMarginTop={shouldSplitHeader ? false : imageMarginTop}
      imageAlignTop={imageAlignTop || shouldSplitHeader}
      hideDefaultHeader
      isFullWidthText={effectiveLayoutVariant === 'full-width'}
      paddingClassName={paddingClassName}
    >
      <SectionHeader
        subtitle={shouldSplitHeader ? undefined : translationContent.subtitle}
        titlePart1={shouldSplitHeader ? '' : translationContent.firstPartOfTitle}
        titlePart2={shouldSplitHeader ? '' : translationContent.secondPartOfTitle}
        titlePart3={shouldSplitHeader ? undefined : translationContent.thirdPartOfTitle}
        contentOnly={shouldSplitHeader}
        description={translationContent.content}
        ctaText={translationContent.ctaText}
        ctaLink={translationContent.ctaLink}
        cta2Text={effectiveCta2Text}
        cta2Link={effectiveCta2Link}
        ctaVariant={effectiveCtaVariant}
        linkText={effectiveLinkText}
        linkUrl={effectiveLinkUrl}
        hasImage={!hideImage}
        isFullWidthDescription={!hideImage && (isLinkCta || layoutVariant !== 'full-width')}
        subDescription={translationContent.subContent}
        subDescriptionClassName="md:max-w-[640px]"
        listItems={translationContent.list}
        gridItems={gridItems !== undefined ? gridItems : (isGridList || isInteractiveRows || translationContent.gridList)}
        listVariant={listVariant}
        listLayout={listLayout}
        interactiveRowsVariant={interactiveRowsVariant || translationContent?.interactiveRowsVariant}
        interactiveRowsSize={interactiveRowsSize || translationContent?.interactiveRowsSize}
        isFullWidthSection={layoutVariant === 'full-width'}
        hideGridMarker={hideGridMarker}
        gridTextSize={gridTextSize}
        centerGridText={centerGridText}
        disableHoverOnMobile={disableHoverOnMobile}
        disableGridHover={disableGridHover}
      />
    </BasicTeaser>
  )
}

export default TeaserWithHighlight
